import {
  buildInitialColorSelectionKeys,
  buildInitialProductSelectionKeys,
  buildStoreCartOptionSelectionKey,
  countSelectedColorsForProduct,
  normalizeStoreCartProductStyleCode,
} from './storeCartDesignOptions.utils';
import {
  isStoreCartLineActive,
  resolveStoreCartItemSelectionFlag,
} from './storeCartLineSelection.utils';
import { getStoreCartDesignGroupKey } from './storeCartDesignGroups.utils';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type { IDesignGroupProductOption } from '@/common/interfaces/design.interface';

export interface IStoreCartDesignGroupSelection {
  selectedColorKeys: string[];
  selectedProductStyleCodes: string[];
}

export const isEmptyStoreCartDesignGroupSelection = (
  selection: IStoreCartDesignGroupSelection | undefined
): boolean =>
  !selection ||
  (selection.selectedColorKeys.length === 0 && selection.selectedProductStyleCodes.length === 0);

export const serializeStoreCartDesignGroupSelection = (
  selection: IStoreCartDesignGroupSelection | undefined
): string => {
  if (!selection) {
    return '';
  }

  const productKeys = [...selection.selectedProductStyleCodes]
    .sort((a, b) => a.localeCompare(b))
    .join(',');
  const colorKeys = [...selection.selectedColorKeys].sort((a, b) => a.localeCompare(b)).join(',');

  return `${productKeys}::${colorKeys}`;
};

export const areStoreCartDesignGroupSelectionsEqual = (
  left: IStoreCartDesignGroupSelection | undefined,
  right: IStoreCartDesignGroupSelection | undefined
): boolean =>
  serializeStoreCartDesignGroupSelection(left) === serializeStoreCartDesignGroupSelection(right);

export const buildStoreCartDesignGroupSelection = (
  items: ICartItemDetail[]
): IStoreCartDesignGroupSelection => ({
  selectedColorKeys: Array.from(
    buildInitialColorSelectionKeys(items.filter(isStoreCartLineActive))
  ),
  selectedProductStyleCodes: Array.from(
    buildInitialProductSelectionKeys(items.filter(isStoreCartLineActive))
  ),
});

export const isCartItemIncludedInStoreOptionSelection = (
  item: ICartItemDetail,
  selection: IStoreCartDesignGroupSelection | undefined
): boolean => {
  if (isEmptyStoreCartDesignGroupSelection(selection)) {
    return true;
  }

  const resolvedSelection = selection as IStoreCartDesignGroupSelection;
  const productKey = normalizeStoreCartProductStyleCode(item.apparelStyleCode);

  if (!resolvedSelection.selectedProductStyleCodes.includes(productKey)) {
    return false;
  }

  const colorKey = buildStoreCartOptionSelectionKey(item.apparelStyleCode, item.designId);

  return resolvedSelection.selectedColorKeys.includes(colorKey);
};

export const filterCartItemsByStoreOptionSelections = (
  items: ICartItemDetail[],
  selectionsByGroupKey: Record<string, IStoreCartDesignGroupSelection>
): ICartItemDetail[] =>
  items.filter((item) => {
    if (!isStoreCartLineActive(item)) {
      return false;
    }

    const groupKey = getStoreCartDesignGroupKey(item);

    return isCartItemIncludedInStoreOptionSelection(item, selectionsByGroupKey[groupKey]);
  });

export const buildStoreCartOptionSelectionsFromCartItems = (
  items: ICartItemDetail[]
): Record<string, IStoreCartDesignGroupSelection> =>
  items
    .filter(isStoreCartLineActive)
    .reduce<Record<string, IStoreCartDesignGroupSelection>>((acc, item) => {
      const groupKey = getStoreCartDesignGroupKey(item);

      if (!acc[groupKey]) {
        acc[groupKey] = {
          selectedColorKeys: [],
          selectedProductStyleCodes: [],
        };
      }

      const colorKey = buildStoreCartOptionSelectionKey(item.apparelStyleCode, item.designId);
      const productKey = normalizeStoreCartProductStyleCode(item.apparelStyleCode);

      if (!acc[groupKey].selectedColorKeys.includes(colorKey)) {
        acc[groupKey].selectedColorKeys.push(colorKey);
      }

      if (!acc[groupKey].selectedProductStyleCodes.includes(productKey)) {
        acc[groupKey].selectedProductStyleCodes.push(productKey);
      }

      return acc;
    }, {});

export const wouldLeaveNoProductOptionsSelected = (
  selectedProductStyleCodes: Set<string>,
  productKeyToRemove: string
): boolean => {
  const nextProductKeys = new Set(selectedProductStyleCodes);
  nextProductKeys.delete(productKeyToRemove);

  return nextProductKeys.size === 0;
};

export const wouldLeaveNoColorOptionsSelected = (
  selectedProductStyleCodes: Set<string>,
  nextColorKeys: Set<string>,
  productOptions: IDesignGroupProductOption[]
): boolean =>
  !productOptions
    .filter((product) =>
      selectedProductStyleCodes.has(normalizeStoreCartProductStyleCode(product.styleCode))
    )
    .some((product) => countSelectedColorsForProduct(nextColorKeys, product) > 0);

export const applyStoreCartOptionSelectionsToCartItems = (
  items: ICartItemDetail[],
  selectionsByGroupKey: Record<string, IStoreCartDesignGroupSelection>
): ICartItemDetail[] =>
  items.map((item) => {
    const groupKey = getStoreCartDesignGroupKey(item);
    const shouldBeActive = isCartItemIncludedInStoreOptionSelection(
      item,
      selectionsByGroupKey[groupKey]
    );

    const nextSelectionFlag = shouldBeActive ? 1 : 0;

    if (
      item.isActive === shouldBeActive &&
      resolveStoreCartItemSelectionFlag(item) === nextSelectionFlag
    ) {
      return item;
    }

    return {
      ...item,
      isActive: shouldBeActive,
      is_selected: nextSelectionFlag,
    };
  });
